import type { ItemSpan, ResolvedItem, ResolvedSection, SpanningItem } from "@keydials/api-client";

/** KEY-859: an item on a day's own segment key — `day-2026-10-12.morning`. Only those take a time span. */
export function isDaySlotKey(key: string): boolean {
  return /^day-\d{4}-\d{2}-\d{2}(\.|$)/.test(key);
}

/** KEY-859: how an item's time reads beside its name — "All day", "10:00–18:00", "10:00" — or null for none. */
export function spanLabel(span: ItemSpan | null | undefined): string | null {
  if (!span) return null;
  if (span.allDay) return "All day";
  if (span.startsAt && span.endsAt) return `${span.startsAt}–${span.endsAt === "00:00" ? "midnight" : span.endsAt}`;
  return span.startsAt ?? null;
}

/** KEY-859: a thing running on into this time of day, as its faint row says it: the thing, and until when. */
export interface ContinuingRow {
  segmentKey: string;
  itemKey: string;
  name: string;
  /** "continues until 19:00", or "all day" when it runs to the end of the day. */
  note: string;
}

/** The rows for one time of day: each spanning entry named from where it is listed (the same day). Calendar names keep
 * their own "09:15 · " prefix off, as the list itself shows them; an entry whose item can't be found is left out. */
export function continuingRows(spanning: SpanningItem[] | null | undefined, section: ResolvedSection | undefined): ContinuingRow[] {
  if (!spanning?.length || !section) return [];
  const rows: ContinuingRow[] = [];
  for (const s of spanning) {
    const item: ResolvedItem | undefined = section.segments.find((g) => g.key === s.segmentKey)?.items.find((i) => i.key === s.itemKey);
    if (!item) continue;
    const name = item.calendar ? item.name.replace(/^[^·]*·\s*/, "") : item.name;
    rows.push({ segmentKey: s.segmentKey, itemKey: s.itemKey, name, note: s.until ? `continues until ${s.until}` : "all day" });
  }
  return rows;
}

/** KEY-859: what the time editor saves — null clears the time. A start with an end at or before it isn't a span. */
export function spanFromForm(allDay: boolean, start: string, end: string): ItemSpan | null | "invalid" {
  if (allDay) return { allDay: true };
  if (!start) return end ? "invalid" : null;
  if (!end) return { startsAt: start };
  const minutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const endMinutes = end === "00:00" ? 24 * 60 : minutes(end);
  return endMinutes > minutes(start) ? { startsAt: start, endsAt: end } : "invalid";
}
